import type { ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ROUTES } from '../../../constants/routes';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  canViewTab,
  type DashboardSection,
  sectionTabForPath,
} from '../../../layouts/DashboardV2Layout/dashboardSections';

const tabHref = (route: string) => `/${ROUTES.DASHBOARD}/${route}`;

const useViewableTabs = (section: DashboardSection) => {
  const permissions = usePermissions();
  return section.tabs.filter((tab) => canViewTab(tab, permissions));
};

/**
 * Switches between a section's sibling pages. A tab the caller can't view
 * is not offered, and with only one left there is nothing to switch, so no strip.
 * `row` puts it in its own padded row at the top of a page, above any filter bar.
 */
export const SectionTabs = ({ section, row = false }: { section: DashboardSection; row?: boolean }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const tabs = useViewableTabs(section);
  const current = sectionTabForPath(section, pathname);

  if (tabs.length < 2) {
    return null;
  }

  const strip = (
    <nav className="section-tabs" aria-label={t(section.labelKey)}>
      {tabs.map((tab) => {
        const active = tab === current;
        return (
          <Link
            key={tab.route}
            to={tabHref(tab.route)}
            className={`section-tab${active ? ' active' : ''}`}
            aria-current={active ? 'page' : undefined}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              {tab.icon}
            </span>
            {t(tab.labelKey)}
          </Link>
        );
      })}
    </nav>
  );

  return row ? <div className="section-tabs-row">{strip}</div> : strip;
};

/**
 * Route element wrapper for a section's tabs. The sidebar entry always lands
 * on the first tab, so someone who can't view the current tab is forwarded
 * to the first one they can; with none viewable the page renders as before
 * and the API answers for itself. A platform-only tab reached by someone in
 * an organization goes to their first viewable tab, else the dashboard home.
 */
export const SectionTabRoute = ({
  section,
  children,
}: {
  section: DashboardSection;
  children: ReactNode;
}) => {
  const { pathname } = useLocation();
  const { has, organization } = usePermissions();
  const tabs = useViewableTabs(section);
  const current = sectionTabForPath(section, pathname);

  if (current && !has(current.permission) && tabs.length > 0) {
    return <Navigate to={tabHref(tabs[0].route)} replace />;
  }
  if (current?.platformOnly && organization) {
    return <Navigate to={tabs.length > 0 ? tabHref(tabs[0].route) : `/${ROUTES.DASHBOARD}`} replace />;
  }
  return <>{children}</>;
};
